import { UserProfileAttributeMetadata } from "@keycloak/keycloak-admin-client/lib/defs/userProfileMetadata";

import { TFunction } from "i18next";
import { get } from "lodash-es";
import { PropsWithChildren, ReactNode } from "react";
import { UseFormReturn, type FieldError } from "react-hook-form";

import {
  UserFormFields,
  fieldName,
  isRequiredAttribute,
  label,
  labelAttribute,
} from "./utils";
import { FormControl, FormHelperText, InputLabel } from "@mui/material";
import HelpItem from "../controls/HelpItem";

export type UserProfileGroupProps = {
  t: TFunction;
  form: UseFormReturn<UserFormFields>;
  attribute: UserProfileAttributeMetadata;
  renderer?: (attribute: UserProfileAttributeMetadata) => ReactNode;
};

export const UserProfileGroup = ({
  t,
  form,
  attribute,
  renderer,
  children,
}: PropsWithChildren<UserProfileGroupProps>) => {
  const helpText = label(
    t,
    attribute.annotations?.["inputHelperTextBefore"] as string,
  );
  const {
    formState: { errors },
  } = form;

  const component = renderer?.(attribute);
  const error = get(errors, fieldName(attribute.name)) as FieldError;

  return (
    <FormControl
      variant="standard"
    >
      <InputLabel
        shrink
        htmlFor={attribute.name}
        required={isRequiredAttribute(attribute)}
      >
        {labelAttribute(t, attribute) || ""}
        {helpText ? (
          <HelpItem helpText={helpText} fieldLabelId={attribute.name!} />
        ) : undefined}
      </InputLabel>
      {component ? (
        <>
          {children}
          {component}
        </>
      ) : (
        children
      )}
      {error && (
        <FormHelperText
          error={!!error}
          data-testid={`${attribute.name}-helper`}
        >{error.message as string}</FormHelperText>
      )}
    </FormControl>
  );
};
